// -----------------------------------------------------------------------------
// SplashPad mascot audio engine
// -----------------------------------------------------------------------------
// Two responsibilities:
//   1. Whoosh / airplane sound effects while the mascot flies between sections.
//   2. Speech (TTS) for each scripted line the mascot delivers on the landing
//      page tour.
//
// Narration uses fixed Murph MP3s generated once with ElevenLabs. Public visitors
// only download static assets; no provider key or synthesis request is sent.
// Web Audio supplies the existing whoosh/impact effects after explicit opt-in.
// The legacy SpeechSynthesis helper remains for other callers, not this tour.
// -----------------------------------------------------------------------------

let _audioCtx = null;

/** Lazily create the shared AudioContext. Browsers require a user gesture
 *  before an AudioContext can play audio, so we create it lazily on first
 *  call — normally kicked off from the "Start Tour" click. */
export function getAudioCtx() {
    if (typeof window === 'undefined') return null;
    if (_audioCtx) return _audioCtx;
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        _audioCtx = new AC();
        return _audioCtx;
    } catch {
        return null;
    }
}

// -----------------------------------------------------------------------------
// HTMLAudio "silent unlock" for Safari / iOS.
//
// WebKit only allows `HTMLMediaElement.play()` with sound when the call chain
// originates from a user gesture. Our voice clips are fetched over the network
// first (see _resolveBlobUrl below), and Safari revokes the gesture token as
// soon as the chain crosses a real async boundary like `fetch`. Result: the
// first `audio.play()` after Start-Tour is rejected with NotAllowedError on
// Safari/iOS — silently killing every voice line while Chromium plays fine.
//
// Fix: play a ~50ms silent WAV *synchronously inside the click handler*. A
// successful gesture-triggered play "audio-activates" the page in WebKit, so
// all later programmatic plays (even after network awaits) are permitted.
// -----------------------------------------------------------------------------
const SILENT_WAV =
    'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
let _htmlAudioUnlocked = false;

export function unlockHtmlAudio() {
    if (_htmlAudioUnlocked) return;
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    try {
        const a = new Audio(SILENT_WAV);
        a.volume = 0.01; // effectively silent, but NOT muted — muted plays don't activate audio on iOS
        const p = a.play();
        if (p && typeof p.then === 'function') {
            p.then(() => {
                _htmlAudioUnlocked = true;
                try { a.pause(); } catch { /* ignore */ }
            }).catch(() => { /* stays locked — the next user gesture retries */ });
        } else {
            _htmlAudioUnlocked = true;
        }
    } catch { /* ignore */ }
}

/** Kick the AudioContext into 'running' state (Safari/iOS require this after
 *  the first user click). Safe to call multiple times.
 *  ALSO synchronously unlocks HTMLAudio playback for Safari/iOS — must stay
 *  the FIRST statement so it runs inside the user-gesture call chain, before
 *  any `await` gives WebKit a chance to drop the gesture token. */
export async function unlockAudio() {
    unlockHtmlAudio();
    const ctx = getAudioCtx();
    if (!ctx) return false;
    if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch { /* ignore */ }
    }
    return ctx.state === 'running';
}

/**
 * Play a short whoosh: pink-ish noise burst that sweeps a low-pass filter
 * from a high cutoff down to a low one, giving the ear the impression of
 * a fast-moving object flying past. Duration ~450ms.
 *
 * @param {object} opts
 * @param {number} [opts.duration=0.45] seconds
 * @param {number} [opts.volume=0.22]   0..1
 * @param {'down'|'up'} [opts.direction='down']
 */
export function playWhoosh({ duration = 0.45, volume = 0.22, direction = 'down' } = {}) {
    const ctx = getAudioCtx();
    if (!ctx || ctx.state !== 'running') return;

    // Noise source — 2-second buffer of white noise (cheaper than generating
    // per call). We reuse this buffer across whooshes.
    const bufferSize = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * 0.6;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    // Band-pass filter to color the noise into a whoosh.
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.Q.value = 1.1;
    const startFreq = direction === 'down' ? 1400 : 300;
    const endFreq   = direction === 'down' ? 300  : 1400;
    const now = ctx.currentTime;
    filter.frequency.setValueAtTime(startFreq, now);
    filter.frequency.exponentialRampToValueAtTime(endFreq, now + duration);

    // Gain envelope — punchy attack, quick tail.
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.05);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    noise.connect(filter).connect(gain).connect(ctx.destination);
    noise.start(now);
    noise.stop(now + duration + 0.05);
}

/**
 * Play a landing "impact" sound — bass thump layered with a short cyan
 * splash tail. Used when the mascot lands after a flight to sell the
 * weight of the landing (Disney animators call this the "hit sound").
 *
 * Structure:
 *   1. Very short low-frequency sine burst (~80Hz) for the "thump."
 *   2. Filtered noise splash tail (~400ms) for the water reverb.
 *   3. A brief high shimmer (~1600Hz) that fades — the "sparkle."
 */
export function playLandingImpact({ volume = 0.32 } = {}) {
    const ctx = getAudioCtx();
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;

    // 1) Bass thump.
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(80, now);
    osc.frequency.exponentialRampToValueAtTime(35, now + 0.18);
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0, now);
    oscGain.gain.linearRampToValueAtTime(volume * 0.9, now + 0.02);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    osc.connect(oscGain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.32);

    // 2) Splash-tail noise (short filtered burst).
    const bufSize = ctx.sampleRate * 0.5;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * 0.7;
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.Q.value = 1.4;
    noiseFilter.frequency.setValueAtTime(2600, now);
    noiseFilter.frequency.exponentialRampToValueAtTime(500, now + 0.35);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0, now);
    noiseGain.gain.linearRampToValueAtTime(volume * 0.55, now + 0.04);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
    noise.connect(noiseFilter).connect(noiseGain).connect(ctx.destination);
    noise.start(now);
    noise.stop(now + 0.5);

    // 3) High shimmer to sparkle the top of the impact.
    const shimmer = ctx.createOscillator();
    shimmer.type = 'triangle';
    shimmer.frequency.setValueAtTime(1600, now);
    shimmer.frequency.exponentialRampToValueAtTime(2400, now + 0.25);
    const shimmerGain = ctx.createGain();
    shimmerGain.gain.setValueAtTime(0, now);
    shimmerGain.gain.linearRampToValueAtTime(volume * 0.15, now + 0.06);
    shimmerGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);
    shimmer.connect(shimmerGain).connect(ctx.destination);
    shimmer.start(now);
    shimmer.stop(now + 0.35);
}

/**
 * Sparkle chime — very quick high-frequency shimmer used when Splash
 * bursts into a section (sells the "magic dust" trail visually and
 * aurally).
 */
export function playSparkle({ volume = 0.14 } = {}) {
    const ctx = getAudioCtx();
    if (!ctx || ctx.state !== 'running') return;
    const now = ctx.currentTime;
    const freqs = [1800, 2400, 3200];
    for (let i = 0; i < freqs.length; i++) {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freqs[i], now + i * 0.04);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, now + i * 0.04);
        g.gain.linearRampToValueAtTime(volume, now + i * 0.04 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.04 + 0.22);
        osc.connect(g).connect(ctx.destination);
        osc.start(now + i * 0.04);
        osc.stop(now + i * 0.04 + 0.25);
    }
}

const _currentAudio = { el: null, cancel: null };

// -----------------------------------------------------------------------------
// Blob URL cache for pre-recorded voice clips.
//
// Some hosting environments (as of 2026-Q1: SplashPad's production static
// server) serve `.mp3` with `Content-Type: application/octet-stream` instead
// of `audio/mpeg`. Browsers refuse to play a generic octet-stream as audio,
// which silently kills every voice clip on production while leaving the
// procedural Whoosh/Sparkle SFX (Web Audio API — file-less) fully working.
//
// Client-side fix: fetch the file as raw bytes, re-wrap in a Blob with the
// correct `type` derived from the file extension, and hand the Audio element
// an object URL. The browser trusts *our* type declaration on the Blob and
// happily decodes/plays it, sidestepping the server's incorrect header.
//
// We cache the resulting object URLs by source URL so revisiting the same
// clip doesn't re-download it. Cleared on page unload.
// -----------------------------------------------------------------------------
const _blobUrlCache = new Map(); // src url  ->  object url (blob:...)
const _blobLoading = new Map();  // src url -> pending fetch promise
const _blobControllers = new Map(); // src url -> abortable network request

const _MIME_BY_EXT = {
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    aac: 'audio/aac',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    oga: 'audio/ogg',
    opus: 'audio/ogg',
    weba: 'audio/webm',
    flac: 'audio/flac',
};

function _mimeFromUrl(url) {
    try {
        const pathname = new URL(url, typeof window !== 'undefined' ? window.location.href : 'https://x').pathname;
        const ext = (pathname.split('.').pop() || '').toLowerCase();
        return _MIME_BY_EXT[ext] || 'audio/mpeg';
    } catch {
        return 'audio/mpeg';
    }
}

async function _resolveBlobUrl(url) {
    if (_blobUrlCache.has(url)) return _blobUrlCache.get(url);
    if (_blobLoading.has(url)) return _blobLoading.get(url);
    const controller = new AbortController();
    _blobControllers.set(url, controller);
    const timeout = setTimeout(() => controller.abort(), 12000);
    const p = (async () => {
        const resp = await fetch(url, { credentials: 'omit', signal: controller.signal });
        if (!resp.ok) throw new Error(`voice-clip-http-${resp.status}`);
        const buf = await resp.arrayBuffer();
        if (controller.signal.aborted) throw new Error('voice-clip-cancelled');
        const objUrl = URL.createObjectURL(new Blob([buf], { type: _mimeFromUrl(url) }));
        _blobUrlCache.set(url, objUrl);
        return objUrl;
    })().finally(() => {
        clearTimeout(timeout);
        // A cancelled request must not remove a newer retry for the same URL.
        if (_blobControllers.get(url) === controller) {
            _blobControllers.delete(url);
            _blobLoading.delete(url);
        }
    });
    _blobLoading.set(url, p);
    return p;
}

if (typeof window !== 'undefined') {
    // Free up object URLs on unload so we don't leak blob storage across
    // reloads. The browser would GC these anyway on tab close, but doing it
    // explicitly is cleaner and helps DevTools' Application panel stay tidy.
    window.addEventListener('pagehide', () => {
        stopRecordedClip();
        for (const controller of _blobControllers.values()) controller.abort();
        _blobControllers.clear();
        _blobLoading.clear();
        for (const objUrl of _blobUrlCache.values()) {
            try { URL.revokeObjectURL(objUrl); } catch { /* ignore */ }
        }
        _blobUrlCache.clear();
    });
}

/**
 * Play one fixed Murph recording. Resolves on natural end or cancellation;
 * rejects on media/network failure so the caller can keep its text visible.
 * Uses fetch + a typed Blob to tolerate hosts serving generic MP3 MIME types.
 * Cancellation is registered before fetching to prevent late playback.
 */
export function playRecordedClip(url, { volume = 1 } = {}) {
    stopRecordedClip();
    return new Promise((resolve, reject) => {
        let settled = false;
        let audio = null;
        let timeout;
        const finish = (error, outcome = 'ended') => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            if (audio) {
                audio.onended = audio.onpause = audio.onerror = null;
                audio.pause();
                audio.removeAttribute('src');
                audio.load();
            }
            if (_currentAudio.cancel === cancel) {
                _currentAudio.cancel = null;
                _currentAudio.el = null;
            }
            if (error) reject(error);
            else resolve(outcome);
        };
        const cancel = () => {
            // Register cancellation BEFORE fetch so late bytes cannot start audio.
            const controller = _blobControllers.get(url);
            controller?.abort();
            _blobControllers.delete(url);
            _blobLoading.delete(url);
            finish(null, 'cancelled');
        };
        _currentAudio.cancel = cancel;
        timeout = setTimeout(() => finish(new Error('audio-play-timeout')), 30000);
        _resolveBlobUrl(url).then((objUrl) => {
            if (settled) return;
            audio = new Audio(objUrl);
            audio.volume = volume;
            audio.preload = 'auto';
            _currentAudio.el = audio;
            audio.onended = () => finish();
            audio.onpause = () => {
                // Browsers emit pause just before ended at EOF. Let ended finish
                // naturally rather than removing the source mid-event sequence.
                if (!audio.ended) finish(null, 'cancelled');
            };
            audio.onerror = () => finish(new Error('audio-load-failed'));
            const pending = audio.play();
            pending?.catch(() => finish(new Error('audio-play-rejected')));
        }).catch((error) => finish(error));
    });
}

/**
 * Warm the blob cache for a list of voice-clip URLs in the background.
 *
 * Why: `playRecordedClip` must otherwise fetch each MP3 at the moment it is
 * needed — right after the Start-Tour click. That network await both delays
 * the first spoken line AND (on Safari/iOS) crosses the async boundary that
 * revokes the user-gesture token, causing `audio.play()` to be rejected.
 * Preloading while Splash idles next to the logo means play() later resolves
 * from cache in a microtask, keeping the gesture chain intact everywhere.
 *
 * Failures are swallowed here; playback retries once on demand, then the
 * tour keeps the corresponding text visible if that request also fails.
 */
export function preloadVoiceClips(urls = []) {
    if (typeof window === 'undefined') return;
    for (const url of urls) {
        if (!url) continue;
        try { _resolveBlobUrl(url).catch(() => { /* ignore */ }); } catch { /* ignore */ }
    }
}

/** Stop any recorded clip currently playing. */
export function stopRecordedClip() {
    _currentAudio.cancel?.();
}

// -----------------------------------------------------------------------------
// SpeechSynthesis wrapper
// -----------------------------------------------------------------------------
//
// Design notes:
//  * We pick a voice ONCE and cache it. Voices load asynchronously in Chrome
//    (voiceschanged event) so we listen for that too.
//  * We prefer English voices that sound young/male; the fallbacks below are
//    ordered by how good they sound on the OSes we've tested.
//  * The utterance is stored in a module-level ref so we can .cancel() when
//    the user pauses / navigates away, avoiding zombie speech.
//

// Male voice hints (case-insensitive substring match against `voice.name`).
// On most OSes voice names include the person's given name or "Male"/"Female"
// suffix. We hard-filter to voices matching this list so Splash never picks
// up a female voice by accident — which happened to a user on production
// (mascot looks male, but browser fell back to a female English voice).
const MALE_VOICE_NAMES = [
    // Male macOS / iOS system voices (both legacy + Enhanced/Premium variants).
    'Aaron', 'Alex', 'Fred', 'Reed', 'Evan', 'Rocko', 'Ralph', 'Bruce',
    'Daniel', 'Junior', 'Tom', 'Arthur', 'Oliver',
    // Male Google TTS voices (Chrome/Android).
    'Google UK English Male', 'Google US English Male', 'Google US English',
    // Male Microsoft voices (Edge/Windows). Names often end with "Online (Natural)".
    'Microsoft Guy', 'Microsoft Andrew', 'Microsoft Christopher',
    'Microsoft David', 'Microsoft Mark', 'Microsoft Brian',
    'Microsoft Roger', 'Microsoft Eric', 'Microsoft Steffan',
];

// Explicit female names we NEVER want to pick, even if higher up in the
// browser's voice list.
const FEMALE_VOICE_BLOCKLIST = [
    'Samantha', 'Victoria', 'Karen', 'Susan', 'Kate', 'Serena', 'Moira',
    'Fiona', 'Tessa', 'Zira', 'Aria', 'Jenny', 'Sonia', 'Ava', 'Emma',
    'Michelle', 'Nova', 'Google UK English Female',
];

const PREFERRED_VOICE_HINTS = [
    // Enhanced/Premium male voices on modern OSes (best quality first).
    'Aaron (Enhanced)', 'Reed (Enhanced)', 'Evan (Enhanced)', 'Rocko (Enhanced)',
    'Microsoft Guy Online (Natural)', 'Microsoft Andrew Online (Natural)',
    'Microsoft Christopher Online (Natural)',
    'Google US English Male', 'Google UK English Male',
    // Legacy male voices as fallback.
    'Aaron', 'Fred', 'Alex', 'Reed', 'Evan',
    'Microsoft Guy', 'Microsoft David', 'Microsoft Andrew',
];

/** Returns true if a voice looks male. Combines the male-name list + a
 *  regex against the voice's own `name`/`voiceURI` and rejects anything
 *  on the female-blocklist. */
function _looksMale(voice) {
    if (!voice) return false;
    const nameLc = String(voice.name || '').toLowerCase();
    const uriLc = String(voice.voiceURI || '').toLowerCase();
    // Reject obvious female voices first.
    for (const bad of FEMALE_VOICE_BLOCKLIST) {
        if (nameLc.includes(bad.toLowerCase()) || uriLc.includes(bad.toLowerCase())) return false;
    }
    if (/female|femme/i.test(nameLc + ' ' + uriLc)) return false;
    // Explicit male markers.
    if (/male|masculin/i.test(nameLc + ' ' + uriLc) && !/female/i.test(nameLc + ' ' + uriLc)) return true;
    // Male-name match.
    for (const good of MALE_VOICE_NAMES) {
        if (nameLc.includes(good.toLowerCase())) return true;
    }
    return false;
}

let _chosenVoice = null;
let _voicesReady = false;

function _pickVoice() {
    if (typeof window === 'undefined' || !window.speechSynthesis) return null;
    const all = window.speechSynthesis.getVoices() || [];
    if (!all.length) return null;
    // First try our ordered preferred list — best-quality male voices win.
    for (const hint of PREFERRED_VOICE_HINTS) {
        const hit = all.find((v) => v.name.includes(hint));
        if (hit && _looksMale(hit)) return hit;
    }
    // Second: any English voice that clears the male filter.
    const englishMale = all.find((v) => /^en([_-]|$)/i.test(v.lang) && _looksMale(v));
    if (englishMale) return englishMale;
    // Third (very unlikely): any voice that clears the male filter regardless
    // of language. Splash speaks English but at least we avoid a female voice.
    const anyMale = all.find(_looksMale);
    if (anyMale) return anyMale;
    // Absolute last resort: whichever English voice comes first — with a
    // console warning so we can spot it in the wild.
    const anyEnglish = all.find((v) => /^en([_-]|$)/i.test(v.lang));
    if (anyEnglish) {
        try {
            // eslint-disable-next-line no-console
            console.warn('[Splash] No male voice available on this device; falling back to:', anyEnglish.name);
        } catch { /* ignore */ }
        return anyEnglish;
    }
    return all[0] || null;
}

export function primeVoices() {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const tryPick = () => {
        _chosenVoice = _pickVoice();
        if (_chosenVoice) _voicesReady = true;
    };
    tryPick();
    // Chrome fires this once the async voice list is ready.
    window.speechSynthesis.addEventListener?.('voiceschanged', tryPick, { once: true });
}

let _currentUtterance = null;

/**
 * Speak a line and return a promise that resolves when the line finishes
 * (or was cancelled). Rejects only on hard TTS errors.
 *
 * @param {string} text
 * @param {object} [opts]
 * @param {number} [opts.rate=1.0]   0.5..2.0
 * @param {number} [opts.pitch=0.9]  0..2 — slightly lowered = teenage hero, not a little kid
 * @param {number} [opts.volume=1]   0..1
 */
export function speak(text, opts = {}) {
    return new Promise((resolve) => {
        if (typeof window === 'undefined' || !window.speechSynthesis) {
            // No TTS available — resolve after a fake delay proportional to
            // the sentence so the UI still advances the tour.
            const est = Math.max(1200, text.length * 55);
            setTimeout(resolve, est);
            return;
        }
        // Cancel any speech in flight before starting a new line.
        try { window.speechSynthesis.cancel(); } catch { /* ignore */ }

        const u = new SpeechSynthesisUtterance(text);
        if (!_chosenVoice && !_voicesReady) primeVoices();
        if (_chosenVoice) u.voice = _chosenVoice;
        u.lang = _chosenVoice?.lang || 'en-US';
        u.rate = opts.rate ?? 1.0;
        u.pitch = opts.pitch ?? 0.9;  // slightly lowered — teenage hero, deeper than a kid, still MALE
        u.volume = opts.volume ?? 1;

        let settled = false;
        const done = () => {
            if (settled) return;
            settled = true;
            _currentUtterance = null;
            resolve();
        };
        u.onend = done;
        u.onerror = done;
        u.oncancel = done;

        _currentUtterance = u;
        try {
            window.speechSynthesis.speak(u);
        } catch {
            done();
        }

        // Chrome bug: speechSynthesis pauses after ~15s. Keep it alive.
        const keepalive = setInterval(() => {
            if (settled || !window.speechSynthesis.speaking) {
                clearInterval(keepalive);
                return;
            }
            try { window.speechSynthesis.pause(); window.speechSynthesis.resume(); } catch { /* ignore */ }
        }, 12000);
    });
}

export function stopSpeaking() {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    _currentUtterance = null;
}
